/**
 * Client integrity probe (report-only).
 *
 * Browser extensions that harvest API responses do it by monkey-patching
 * `window.fetch` / `XMLHttpRequest` in the page world, and headless
 * automation (Puppeteer/Playwright, "AI browser agents") exposes
 * `navigator.webdriver`. Neither can be *blocked* from the page — an
 * extension the user installed outranks the page — but both are detectable,
 * and an audit trail per user is what lets an admin see who is pulling data
 * with tooling instead of by hand.
 *
 * Snapshot early (main.tsx, before React) so a patch applied by a
 * `document_start` content script is caught even if it later restores the
 * native; report once per tab session after login, only when something
 * looks off (quiet tabs send nothing).
 */

export interface ClientIntegritySignals {
  patchedFetch: boolean;
  patchedXhrOpen: boolean;
  patchedXhrSend: boolean;
  patchedResponseJson: boolean;
  patchedJsonParse: boolean;
  webdriver: boolean;
  extensionAssets: number;
  checkedAt: "boot" | "report";
}

function isNative(fn: unknown): boolean {
  if (typeof fn !== "function") return false;
  try {
    return /\{\s*\[native code\]\s*\}\s*$/.test(
      Function.prototype.toString.call(fn),
    );
  } catch {
    // A patched Function.prototype.toString that throws is itself a signal.
    return false;
  }
}

function probe(checkedAt: ClientIntegritySignals["checkedAt"]): ClientIntegritySignals {
  const w = window as any;
  let extensionAssets = 0;
  try {
    extensionAssets = document.querySelectorAll(
      'script[src^="chrome-extension://"],script[src^="moz-extension://"],link[href^="chrome-extension://"],link[href^="moz-extension://"]',
    ).length;
  } catch {
    /* ignore */
  }
  return {
    patchedFetch: !isNative(w.fetch),
    patchedXhrOpen: !isNative(w.XMLHttpRequest?.prototype?.open),
    patchedXhrSend: !isNative(w.XMLHttpRequest?.prototype?.send),
    patchedResponseJson: !isNative(w.Response?.prototype?.json),
    patchedJsonParse: !isNative(w.JSON?.parse),
    webdriver: navigator.webdriver === true,
    extensionAssets,
    checkedAt,
  };
}

let bootSnapshot: ClientIntegritySignals | null = null;

/** Call once from main.tsx, before React mounts. Never throws. */
export function snapshotClientIntegrity(): void {
  if (typeof window === "undefined" || bootSnapshot) return;
  try {
    bootSnapshot = probe("boot");
  } catch {
    bootSnapshot = null;
  }
}

function anySignal(s: ClientIntegritySignals): boolean {
  return (
    s.patchedFetch ||
    s.patchedXhrOpen ||
    s.patchedXhrSend ||
    s.patchedResponseJson ||
    s.patchedJsonParse ||
    s.webdriver ||
    s.extensionAssets > 0
  );
}

const SESSION_FLAG = "xale_ci_reported";

/**
 * Post the probe result once per tab session, only when a signal fired at
 * boot or now. `send` is injected so this module has no axios dependency
 * (main.tsx imports it before the API layer is set up).
 */
export async function reportClientIntegrity(
  send: (payload: {
    boot: ClientIntegritySignals | null;
    now: ClientIntegritySignals;
    userAgent: string;
  }) => Promise<unknown>,
): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(SESSION_FLAG)) return;
  } catch {
    /* sessionStorage unavailable: fall through and report at most once per load */
  }
  let now: ClientIntegritySignals;
  try {
    now = probe("report");
  } catch {
    return;
  }
  if (!anySignal(now) && !(bootSnapshot && anySignal(bootSnapshot))) return;
  try {
    sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    /* ignore */
  }
  try {
    await send({ boot: bootSnapshot, now, userAgent: navigator.userAgent });
  } catch {
    // Report-only: never surface to the user.
  }
}
